import React, { useEffect, useRef } from "react";
import { GroupChevron, Hyperlink } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { badge } from "~/shared/helpers";
import {
  locationCode,
  locationNameLabel,
  locationPriorityKey,
  locationWorktime,
} from "../lib/cascade-rows";

/**
 * Contents of a pickup location's group header row: name on the left, then the
 * location's summary (type, container-group count, worktime, priority, code)
 * pushed to the right edge. The row itself toggles the group; the name is its
 * own click target and opens the location modal.
 */
export const PickupLocationGroupHeader: React.FC<{
  location: ConnectProto.PickupLocation;
  isExpanded: boolean;
  /** The location a deep link points at: it brings itself into view. */
  isHighlighted?: boolean;
  onNameClick: () => void;
}> = ({ location, isExpanded, isHighlighted, onNameClick }) => {
  const t = useT();
  const cgCount = location.containerGroups?.length ?? 0;

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isHighlighted) ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [isHighlighted]);

  const priorityKey = locationPriorityKey(location);
  const priorityText = `${t(Namespace.Enum, priorityKey)} ${t(
    Namespace.Table,
    "priority",
  ).toLocaleLowerCase()}`;

  return (
    <div
      ref={ref}
      data-testid={`pickup-location-${location.id}`}
      className="flex flex-1 items-center justify-between gap-4 pl-3 pr-4 h-full whitespace-nowrap min-w-0"
    >
      <div className="flex items-center gap-4 min-w-0">
        {cgCount > 0 && <GroupChevron isExpanded={isExpanded} size="md" />}
        {/* The row around this is the group's toggle, so the link's own
            Enter/Space activation must not also collapse the group. */}
        <span onKeyDown={(event) => event.stopPropagation()} className="min-w-0">
          <Hyperlink className="truncate" onOpen={onNameClick}>
            {locationNameLabel(location, t)}
          </Hyperlink>
        </span>
      </div>

      <div className="flex items-center gap-7 text-sm font-normal text-icon-secondary">
        {/* A location with no type shows none, not even a placeholder. */}
        {location.pickupLocationType?.name && <span>{location.pickupLocationType.name}</span>}
        <span>
          {t(Namespace.PickupLocation, "cascade.containerGroupsCount", { count: cgCount })}
        </span>
        <span>{locationWorktime(location, t)}</span>
        {badge(priorityText, priorityKey, { empty: null, className: "min-h-6" })}
        <span className="ml-2">{locationCode(location)}</span>
      </div>
    </div>
  );
};
